import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Environment } from '@core/services/common';
import { Platform } from '@ionic/angular';
import { Policy, PolicyTypeInUrlPath } from '@models/policy';
import { DeviceDetectorService } from 'ngx-device-detector';
import { v4 as uuidv4 } from 'uuid';
import { MMobileHelperService } from './mmobile-helper.service';

export enum HttpStatusCode {
  OFFLINE = 0,
  SUCCESS = 200,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  TIMEOUT = 408,
  PRECONDITION_FAILED = 412,
  INVALID_SESSION = 419,
  SERVER_ERROR = 500
}

@Injectable({
  providedIn: 'root'
})
export class BaseService {
  constructor(
    private mmHelper: MMobileHelperService,
    private deviceService: DeviceDetectorService,
    private platform: Platform
  ) {}

  getPrivateOIMBaseUrl() {
    return this.mmHelper.getServiceUrl('middlewarePrivateOIMServices');
  }

  getPrivateTeCuidamosBaseUrl() {
    return this.mmHelper.getServiceUrl('middlewarePrivateTeCuidamosServices');
  }

  getPublicBaseUrl() {
    return this.mmHelper.getServiceUrl('middlewarePublicServices');
  }

  getPublicServicesLocator() {
    return this.mmHelper.getServiceUrl(
      'middlewarePublicServiceLocatorServices'
    );
  }

  getPolicyTypeBaseUrl(policy: Policy, path: string): string {
    const policyPathName = PolicyTypeInUrlPath[policy.policyBaseType];
    const policyNumber = policy.policyNumber;
    const baseUrl = this.getPrivateOIMBaseUrl();
    return `${baseUrl}/${policyPathName}/${policyNumber}/${path}`;
  }

  getSdmApiAuthBaseUrl() {
    return Environment.config().sdmApiAuthUrl;
  }

  getSdmApiMetsBaseUrl() {
    return Environment.config().sdmApiMetsUrl;
  }

  getAwsBaseUrl() {
    return this.mmHelper.getServiceUrl('middlewareAWSServices');
  }

  getClientCertificateService() {
    return this.mmHelper.getServiceUrl('clientCertificateService');
  }

  getMultiMapBaseUrl() {
    return this.mmHelper.getServiceUrl('multiMapServices');
  }

  getTeCuidamosSaviaBaseUrl() {
    return this.mmHelper.getServiceUrl('teCuidamosSaviaServices');
  }

  getPublicGeneralConditions() {
    return this.mmHelper.getServiceUrl(
      'generalConditionsPdf'
    );
  }

  getPrivateOIMHeaders(opts?: {
    cacheResponse?: boolean;
    clearCache?: boolean;
  }) {
    let headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('X-Version-Id', this.mmHelper.getVersionId())
      .append('X-Request-Id', uuidv4());
    headers = this.setCacheInterceptorHeader(headers, opts);
    return { headers };
  }

  getPublicHeadersWithToken(
    token: string,
    opts?: { cacheResponse?: boolean; clearCache?: boolean }
  ) {
    let headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('X-Authorization', `Bearer ${token}`)
      .append('X-Version-Id', this.mmHelper.getVersionId())
      .append('X-Request-Id', uuidv4());
    headers = this.setCacheInterceptorHeader(headers, opts);
    return { headers };
  }

  getPublicPendingReceiptListHeaders(
    phoneNumber,
    token: string,
    opts?: { cacheResponse: boolean; clearCache?: boolean }
  ) {
    let headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('Authorization', 'Basic ' + btoa(`${phoneNumber}:${token}`))
      .append('X-Version-Id', this.mmHelper.getVersionId())
      .append('X-Request-Id', uuidv4());
    headers = this.setCacheInterceptorHeader(headers, opts);
    return { headers };
  }

  getPublicProfilePinHeaders(
    phoneNumber: string,
    token: string,
    userToken: string,
    opts?: { cacheResponse: boolean; clearCache?: boolean }
  ) {
    let headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('X-Authorization', `Bearer ${userToken}`)
      .append('Authorization', 'Basic ' + btoa(`${phoneNumber}:${token}`))
      .append('X-Version-Id', this.mmHelper.getVersionId())
      .append('X-Request-Id', uuidv4());
    headers = this.setCacheInterceptorHeader(headers, opts);
    return { headers };
  }

  getPublicHeaders(opts?: {
    cacheResponse: boolean;
    clearCache?: boolean;
  }): {
    headers?:
      | HttpHeaders
      | {
          [header: string]: string | string[];
        };
    observe?: 'body';
    params?:
      | HttpParams
      | {
          [param: string]: string | string[];
        };
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
  } {
    let headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('X-Version-Id', this.mmHelper.getVersionId())
      .append('X-Request-Id', uuidv4());
    headers = this.setCacheInterceptorHeader(headers, opts);
    return { headers };
  }

  getSdmApiAuthHeaders(origin: string, loginType: string) {
    let productId = 'Web';

    if (this.platform.is('cordova')) {
      productId = this.platform.is('android') ? 'Android' : 'IOS';
    }

    return new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('X-MMOBILE-App-Name', 'mets')
      .append('X-MMOBILE-Config-Name', 'JWTOIM')
      .append('X-MMOBILE-App-Version', this.mmHelper.getVersionId())
      .append('X-RO-Login-Type', loginType)
      .append('X-RO-Device-Model', this.platform.is('cordova') ? this.deviceService.device : this.deviceService.browser)
      .append('X-RO-Version-OS', this.platform.is('cordova') ? this.deviceService.os_version : this.deviceService.browser_version)
      .append('X-RO-Id-Product', productId)
      .append('X-RO-Origin', origin);
  }

  getSdmPrivateHeaders(token: string) {
    return new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('Authorization', `Bearer ${token}`)
      .append('X-MMOBILE-App-Name', 'mets')
      .append('X-MMOBILE-App-Version', this.mmHelper.getVersionId());
  }

  getLoginHeaders() {
    return new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('MMOBILE_JWT_ACCEPT_TOKEN', 'header')
      .append('X-NGX-CACHE-INTERCEPTOR', 'clear-cache')
      .append('X-Version-Id', this.mmHelper.getVersionId())
      .append('X-Request-Id', uuidv4());
  }

  setCacheInterceptorHeader(headers: HttpHeaders, opts?: { cacheResponse?: boolean; clearCache?: boolean; }) {
    if (opts) {
      let value = '';
      if (opts.clearCache && opts.cacheResponse) {
        value = 'clear-cache, cache-response';
      } else if (opts.clearCache) {
        value = 'clear-cache';
      } else if (opts.cacheResponse) {
        value = 'cache-response';
      } else {
        return headers;
      }
      return headers.append('X-NGX-CACHE-INTERCEPTOR', value);
    }
    return headers;
  }
}
